import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { DataSet, Network, type Edge as VisEdge, type Node as VisNode } from "vis-network/standalone";
import type { WorkspaceVisualization } from "@/api/knowledgeGraphApi";
import { getKnowledgeGraphVisualization, getWorkspaceVisualization } from "@/api/knowledgeGraphApi";
import NodeHoverTooltip from "@/features/graph/components/NodeHoverTooltip";
import { shiftHexColor, useGraphTheme } from "@/features/graph/hooks/useGraphTheme";
import { useNodeHoverTooltip } from "@/features/graph/hooks/useNodeHoverTooltip";
import { useVisNetworkBuilders } from "@/features/graph/hooks/useVisNetworkBuilders";
import type { KnowledgeGraphVisualization, VisualEdge, VisualNode } from "@/types/knowledgeGraph";
import {
	calculateBetweennessCentrality,
	calculateDegreeCentrality,
	calculateEigenvectorCentrality,
	calculateNodeSizes,
	detectCommunitiesBFS,
	getCommunityColor,
} from "@/utils/graphAnalysis";

interface WorkspaceViewProps {
	workspaceId: string;
	onKgSelect?: (graphId: string) => void;
	onNodeSelect?: (nodeId: string) => void;
	/** When true, the first KG in the workspace is expanded on load; otherwise all start collapsed. */
	expanded?: boolean;
}

export default function WorkspaceView({ workspaceId, onNodeSelect, expanded = false }: WorkspaceViewProps) {
	const { graphTheme, theme } = useGraphTheme();
	const { buildVisNode, buildVisEdge, networkOptions } = useVisNetworkBuilders();

	const containerRef = useRef<HTMLDivElement>(null);
	const networkRef = useRef<Network | null>(null);

	// Persistent DataSets — updated in place so the network reacts without full recreation.
	const nodesDSRef = useRef(new DataSet<VisNode>());
	const edgesDSRef = useRef(new DataSet<VisEdge>());

	// IDs of workspace-level KG "super-nodes".
	const wsKgNodeIdsRef = useRef<Set<string>>(new Set());

	// Which KG super-nodes are currently expanded.
	const expandedKgIdsRef = useRef<Set<string>>(new Set());

	// Cache for fetched KG visualization payloads.
	const kgCacheRef = useRef<Map<string, KnowledgeGraphVisualization>>(new Map());

	// Per-KG-super-node computed colour, stored so collapse can restore exactly.
	const wsNodeTonesRef = useRef<Map<string, string>>(new Map());

	// Stable refs for values used inside async/imperative code.
	const buildVisNodeRef = useRef(buildVisNode);
	const buildVisEdgeRef = useRef(buildVisEdge);
	const graphThemeRef = useRef(graphTheme);
	const themeRef = useRef(theme);
	const onNodeSelectRef = useRef(onNodeSelect);
	useEffect(() => {
		buildVisNodeRef.current = buildVisNode;
		buildVisEdgeRef.current = buildVisEdge;
		graphThemeRef.current = graphTheme;
		themeRef.current = theme;
		onNodeSelectRef.current = onNodeSelect;
	}, [buildVisNode, buildVisEdge, graphTheme, theme, onNodeSelect]);

	const {
		data: workspaceData,
		isLoading,
		isError,
	} = useQuery({
		queryKey: ["workspace", workspaceId],
		queryFn: ({ signal }) => getWorkspaceVisualization(workspaceId, { signal }),
		staleTime: 30_000,
	});

	const getNodeData = useCallback(
		(nodeId: string) => {
			// Internal expanded node: ${kgId}__n__${realNodeId}
			if (nodeId.includes("__n__")) {
				const [kgId, realNodeId] = nodeId.split("__n__");
				if (!kgId || !realNodeId) return null;
				const cached = kgCacheRef.current.get(kgId)?.nodes.find((n) => n.id === realNodeId);
				return cached ? { name: cached.name, elementType: cached.elementType, metadata: cached.metadata } : null;
			}
			// Workspace-level KG super-node
			const kg = workspaceData?.nodes.find((n) => n.id === nodeId);
			if (!kg) return null;
			const isExpanded = expandedKgIdsRef.current.has(nodeId);
			return {
				name: kg.name,
				elementType: "Knowledge Graph",
				metadata: {
					...(kg.description ? { description: kg.description } : {}),
					classes: kg.classCount,
					...(kg.classLabels.length > 0 ? { types: kg.classLabels.join(", ") } : {}),
				},
				hint: isExpanded ? "Click to collapse" : "Click to expand",
			};
		},
		[workspaceData],
	);
	const { registerHoverEvents, clearHover, hoveredNode, tooltipPos } = useNodeHoverTooltip({
		containerRef,
		getNodeData,
	});

	// ── Populate DataSets whenever workspace data or theme changes ──────────────
	useEffect(() => {
		if (!workspaceData) return;
		const gt = graphTheme;
		const isDark = theme === "dark";

		// Clear everything (theme change collapses expanded KGs).
		nodesDSRef.current.clear();
		edgesDSRef.current.clear();
		expandedKgIdsRef.current.clear();
		wsNodeTonesRef.current.clear();

		// Normalise classCount for the same radius-based colour-shift used in KnowledgeGraphCanvas.
		const counts = workspaceData.nodes.map((n) => n.classCount);
		const minCount = Math.min(...counts, 0);
		const maxCount = Math.max(...counts, 1);
		const countSpread = maxCount - minCount;

		const wsNodeIds = new Set<string>();
		const visNodes: VisNode[] = workspaceData.nodes.map((kg) => {
			wsNodeIds.add(kg.id);
			const size = Math.max(20, Math.min(20 + kg.classCount * 3, 60));
			const normalized = countSpread > 0 ? (kg.classCount - minCount) / countSpread : 0.5;
			const shiftAmount = isDark ? -0.42 + normalized * 0.7 : -0.21 + normalized * 0.49;
			const nodeTone = shiftHexColor(gt.accent, shiftAmount);
			wsNodeTonesRef.current.set(kg.id, nodeTone);
			return {
				id: kg.id,
				label: kg.name,
				shape: "dot",
				size,
				color: {
					background: nodeTone,
					border: gt.borderStrong,
					highlight: { background: gt.accentSoft, border: gt.accent },
					hover: { background: shiftHexColor(nodeTone, 0.08), border: gt.accent },
				},
				font: {
					size: 13,
					face: "Inter, system-ui, -apple-system, sans-serif",
					color: gt.text,
					strokeWidth: 2,
					strokeColor: gt.surface,
					align: "center" as const,
				},
				borderWidth: 2.5,
				borderWidthSelected: 4,
				shadow: {
					enabled: true,
					color: isDark ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.15)",
					size: 8,
					x: 2,
					y: 2,
				},
				margin: { top: 10, right: 10, bottom: 10, left: 10 },
			};
		});
		wsKgNodeIdsRef.current = wsNodeIds;

		const visEdges: VisEdge[] = workspaceData.edges.map((e) => buildWsEdge(e, gt));
		nodesDSRef.current.add(visNodes);
		edgesDSRef.current.add(visEdges);
	}, [workspaceData, graphTheme, theme]);

	// ── Expand / collapse a KG super-node in-place ──────────────────────────────
	const handleToggleExpand = useCallback(async (kgId: string) => {
		const gt = graphThemeRef.current;
		const isDark = themeRef.current === "dark";

		if (expandedKgIdsRef.current.has(kgId)) {
			// Collapse: remove namespaced internal nodes/edges.
			const nodeIdsToRemove = nodesDSRef.current.getIds().filter((id) => String(id).startsWith(`${kgId}__n__`));
			const edgeIdsToRemove = edgesDSRef.current.getIds().filter((id) => String(id).startsWith(`${kgId}__e__`));
			nodesDSRef.current.remove(nodeIdsToRemove as string[]);
			edgesDSRef.current.remove(edgeIdsToRemove as string[]);
			expandedKgIdsRef.current.delete(kgId);

			// Restore super-node using the exact per-node colour computed on initial build.
			const nodeTone = wsNodeTonesRef.current.get(kgId) ?? shiftHexColor(gt.accent, 0);
			nodesDSRef.current.update({
				id: kgId,
				borderWidth: 2.5,
				color: {
					background: nodeTone,
					border: gt.borderStrong,
					highlight: { background: gt.accentSoft, border: gt.accent },
					hover: { background: shiftHexColor(nodeTone, 0.08), border: gt.accent },
				},
			});
		} else {
			// Expand: fetch visualization (or use cache), then add internal nodes/edges.
			let vizData = kgCacheRef.current.get(kgId);
			if (!vizData) {
				try {
					vizData = await getKnowledgeGraphVisualization(kgId);
					kgCacheRef.current.set(kgId, vizData);
				} catch {
					return;
				}
			}

			const pos = networkRef.current?.getPosition(kgId) ?? { x: 0, y: 0 };
			const rawNodes = vizData.nodes as unknown as VisualNode[];
			const rawEdges = vizData.edges as unknown as VisualEdge[];

			// Compute the same metrics as useGraphMetrics so radius and colour match KnowledgeGraphCanvas.
			const degree = calculateDegreeCentrality(rawNodes, rawEdges);
			const betweenness = calculateBetweennessCentrality(rawNodes, rawEdges);
			const eigenvector = calculateEigenvectorCentrality(rawNodes, rawEdges);
			const nodeSizes = calculateNodeSizes(rawNodes, degree, betweenness, eigenvector);
			const communities = detectCommunitiesBFS(rawNodes, rawEdges);

			const kgNodes: VisualNode[] = rawNodes.map((node) => ({
				...node,
				radius: (nodeSizes.get(node.id) ?? 20) / 2,
				color: getCommunityColor(communities.get(node.id) ?? 0),
				group: communities.get(node.id) ?? 0,
			}));
			const kgEdges: VisualEdge[] = rawEdges.map((edge) => ({
				...edge,
				strength: 0.3,
				inferred: edge.inferred ?? false,
			}));

			// Normalise radii for the colour-shift (identical to nodeDisplayColors in KnowledgeGraphCanvas).
			const radii = kgNodes.map((n) => n.radius ?? 12);
			const minR = Math.min(...radii);
			const maxR = Math.max(...radii);
			const spread = maxR - minR;

			// Seed all nodes at the super-node's canvas position with a tiny random jitter so
			// the physics engine has distinct starting points and pushes them outward naturally.
			const newVisNodes: VisNode[] = kgNodes.map((node) => {
				const normalizedRadius = spread > 0 ? ((node.radius ?? 12) - minR) / spread : 0.5;
				return buildVisNodeRef.current(node, {
					idOverride: `${kgId}__n__${node.id}`,
					pos: {
						x: pos.x + (Math.random() - 0.5) * 8,
						y: pos.y + (Math.random() - 0.5) * 8,
					},
					normalizedRadius,
				});
			});

			const newVisEdges: VisEdge[] = kgEdges.map((edge) => {
				const source = typeof edge.source === "string" ? edge.source : (edge.source as VisualNode).id;
				const target = typeof edge.target === "string" ? edge.target : (edge.target as VisualNode).id;
				return buildVisEdgeRef.current(edge, {
					idOverride: `${kgId}__e__${edge.id}`,
					fromOverride: `${kgId}__n__${source}`,
					toOverride: `${kgId}__n__${target}`,
				});
			});

			nodesDSRef.current.add(newVisNodes);
			edgesDSRef.current.add(newVisEdges);
			expandedKgIdsRef.current.add(kgId);

			// Style the super-node to indicate it is expanded.
			nodesDSRef.current.update({
				id: kgId,
				borderWidth: 5,
				color: {
					background: shiftHexColor(gt.accent, 0.2),
					border: gt.accent,
					highlight: { background: gt.accentSoft, border: gt.accent },
					hover: {
						background: shiftHexColor(gt.accent, 0.25),
						border: isDark ? "#ff8a6b" : "#ff441b",
					},
				},
			});

			// After physics settles, zoom to fit all nodes.
			networkRef.current?.fit({ animation: { duration: 1500, easingFunction: "easeInOutQuad" } });
		}
	}, []);

	// ── When expanded prop is true, expand the first KG after data loads ────────
	const initialExpandedDoneRef = useRef(false);
	const prevWorkspaceDataRef = useRef(workspaceData);
	if (prevWorkspaceDataRef.current !== workspaceData) {
		prevWorkspaceDataRef.current = workspaceData;
		initialExpandedDoneRef.current = false;
	}
	useEffect(() => {
		if (!workspaceData || !expanded || initialExpandedDoneRef.current || workspaceData.nodes.length === 0) return;
		const firstId = workspaceData.nodes[0].id;
		if (!wsKgNodeIdsRef.current.has(firstId)) return;
		initialExpandedDoneRef.current = true;
		void handleToggleExpand(firstId);
	}, [workspaceData, expanded, handleToggleExpand]);

	// ── Initialise vis-network (recreated when physics/options change) ───────────
	// The DataSets are refs, so their current data persists across recreations.
	useEffect(() => {
		if (!containerRef.current) return;

		const network = new Network(
			containerRef.current,
			{ nodes: nodesDSRef.current, edges: edgesDSRef.current },
			networkOptions,
		);
		networkRef.current = network;

		registerHoverEvents(network);

		network.on("click", (params: { nodes: (string | number)[] }) => {
			if (params.nodes.length === 0) return;
			const nodeId = String(params.nodes[0]);
			if (wsKgNodeIdsRef.current.has(nodeId)) {
				// Workspace-level KG super-node: expand/collapse.
				void handleToggleExpand(nodeId);
			} else if (nodeId.includes("__n__")) {
				// Internal expanded node: propagate selection.
				const realNodeId = nodeId.split("__n__")[1];
				if (realNodeId) onNodeSelectRef.current?.(realNodeId);
			}
		});

		return () => {
			clearHover();
			network.destroy();
			networkRef.current = null;
		};
	}, [networkOptions, handleToggleExpand, registerHoverEvents, clearHover]);

	return (
		<div className="relative h-full w-full">
			{isLoading && (
				<div className="absolute inset-0 flex items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			)}
			{isError && (
				<div className="absolute inset-0 flex items-center justify-center">
					<p className="text-sm text-red-500">Failed to load workspace.</p>
				</div>
			)}
			{!isLoading && !isError && workspaceData?.nodes.length === 0 && (
				<div className="absolute inset-0 flex items-center justify-center">
					<p className="text-sm text-muted">No knowledge graphs yet.</p>
				</div>
			)}
			<div ref={containerRef} className="h-full w-full" />
			{hoveredNode && tooltipPos && (
				<NodeHoverTooltip
					name={hoveredNode.name}
					elementType={hoveredNode.elementType}
					metadata={hoveredNode.metadata}
					position={tooltipPos}
					hint={hoveredNode.hint}
				/>
			)}
		</div>
	);
}

function buildWsEdge(
	e: WorkspaceVisualization["edges"][number],
	gt: ReturnType<typeof useGraphTheme>["graphTheme"],
): VisEdge {
	return {
		id: e.id,
		from: e.source,
		to: e.target,
		label: `${e.sharedCount} shared`,
		title: e.sharedLabels.join(", "),
		dashes: [6, 4],
		arrows: { to: { enabled: false } },
		color: { color: gt.borderStrong, highlight: gt.accentSoft, hover: gt.accent, opacity: 0.7 },
		font: {
			size: 11,
			face: "Inter, system-ui, sans-serif",
			color: gt.textMuted,
			align: "middle" as const,
			strokeWidth: 2,
			strokeColor: gt.surface,
		},
		width: 2,
		selectionWidth: 3,
		hoverWidth: 3,
		smooth: { enabled: true, type: "continuous" as const, roundness: 0.5 },
	};
}
